// =============================================================================
// VandieZ admin-web -- SMS dispatch log detail (/settings/sms/log/:logId)
// =============================================================================
// Single-row view backed by GET /admin/sms-config/_log/{log_id}.
// Surfaces the fields the list view omits (full error_message body +
// the recipient's name via a backend JOIN) so the support team can
// triage a failed SMS without scraping the DB.
// =============================================================================

import {
  AlertCircle,
  ArrowLeft,
  Loader2,
  MessageSquare,
} from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { smsAdminApi } from "@/api/client";
import { PageHeader } from "@/components/AdminLayout";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { ApiError } from "@/types/auth";
import type { SmsDispatchLogDetail } from "@/types/sms";

const STATUS_PILL: Record<string, string> = {
  queued: "bg-muted text-muted-foreground",
  sent: "bg-primary-subtle text-primary-subtle-foreground",
  delivered: "bg-success-subtle text-success-subtle-foreground",
  failed: "bg-danger-subtle text-danger-subtle-foreground",
  budget_exceeded: "bg-warning-subtle text-warning-subtle-foreground",
  skipped: "bg-muted text-muted-foreground",
};

function formatDateTime(iso: string | null): string {
  if (iso === null) {return "—";}
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

export function SmsLogDetailPage(): React.JSX.Element {
  const { logId } = useParams<{ logId: string }>();
  const { accessToken } = useAuth();
  const [row, setRow] = useState<SmsDispatchLogDetail | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRow = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      if (accessToken === null || logId === undefined) {return;}
      setLoading(true);
      setError(null);
      try {
        const res = await smsAdminApi.logDetail(accessToken, logId, signal);
        setRow(res);
      } catch (e: unknown) {
        if (e instanceof Error && e.name === "AbortError") {return;}
        if (e instanceof ApiError) {
          setError(e.problem.detail);
        } else {
          setError("Unexpected error. Try again.");
        }
      } finally {
        setLoading(false);
      }
    },
    [accessToken, logId],
  );

  useEffect(() => {
    const c = new AbortController();
    void fetchRow(c.signal);
    return () => { c.abort(); };
  }, [fetchRow]);

  return (
    <div className="space-y-4">
      <PageHeader
        title="SMS dispatch detail"
        description={
          <Link
            to="/settings/sms/log"
            className="inline-flex items-center text-sm hover:underline"
          >
            <ArrowLeft className="mr-1 h-3 w-3" />
            Back to dispatch log
          </Link>
        }
      />

      {error !== null && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {loading || row === null ? (
        <div className="flex items-center gap-2 py-12 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading…
        </div>
      ) : (
        <>
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <MessageSquare className="h-5 w-5" />
                    {row.event_key}
                  </CardTitle>
                  <CardDescription>
                    Sent to{" "}
                    <span className="font-medium">
                      {row.user_name ?? "(unknown user)"}
                    </span>
                    <span className="font-mono text-xs ml-1">
                      ({row.phone})
                    </span>
                  </CardDescription>
                </div>
                <span
                  className={cn(
                    "inline-flex rounded-full px-2 py-0.5 text-xs font-medium capitalize",
                    STATUS_PILL[row.status] ?? "bg-muted text-muted-foreground",
                  )}
                >
                  {row.status.replace(/_/g, " ")}
                </span>
              </div>
            </CardHeader>
            <CardContent>
              <dl className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <Field label="Event key" value={row.event_key} />
                <Field label="Language" value={row.language.toUpperCase()} />
                <Field label="Provider" value={row.provider} />
                <Field
                  label="Provider msg id"
                  value={row.provider_message_id ?? "—"}
                />
                <Field
                  label="Cost (INR)"
                  value={row.cost_inr !== null ? `₹${row.cost_inr}` : "—"}
                />
                <Field
                  label="Sent at"
                  value={formatDateTime(row.sent_at)}
                />
                <Field
                  label="Delivered at"
                  value={formatDateTime(row.delivered_at)}
                />
                <Field
                  label="User id"
                  value={
                    <Link
                      to={`/users/${row.user_id}`}
                      className="font-mono text-xs hover:underline"
                    >
                      {row.user_id.slice(0, 8)}…
                    </Link>
                  }
                />
              </dl>
            </CardContent>
          </Card>

          {(row.error_code !== null || row.error_message !== null) && (
            <Card>
              <CardHeader>
                <CardTitle className="text-danger-subtle-foreground">Error</CardTitle>
                <CardDescription>
                  Raw failure response from the SMS provider.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {row.error_code !== null && (
                  <div className="text-sm">
                    <span className="text-xs uppercase tracking-wide text-muted-foreground mr-2">
                      Code
                    </span>
                    <span className="font-mono">{row.error_code}</span>
                  </div>
                )}
                {row.error_message !== null && (
                  <pre className="bg-muted/40 rounded-md p-3 text-xs whitespace-pre-wrap break-words">
                    {row.error_message}
                  </pre>
                )}
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

function Field({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}): React.JSX.Element {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="text-sm font-medium break-words">{value}</div>
    </div>
  );
}
